import { useDispatch, useSelect } from '@wordpress/data';
import { store as blocksStore } from '@wordpress/blocks';
import { ToolbarButton, ToolbarGroup } from '@wordpress/components';
import { group, row, stack, grid } from '@wordpress/icons';
import { _x } from '@wordpress/i18n';
import { useConvertToGroupButtonProps } from '../convert-to-group-buttons';
import { store as blockEditorStore } from '../../store';
import { groupBlocks } from '../../utils/group-blocks';
import type { Layout } from '../../layouts/types';

const layouts: Record< string, Layout > = {
	group: { type: 'constrained' },
	row: { type: 'flex', flexWrap: 'nowrap' },
	stack: { type: 'flex', orientation: 'vertical' },
	grid: { type: 'grid' },
};

function BlockGroupToolbar() {
	const { blocksSelection, clientIds, groupingBlockName, isGroupable } =
		useConvertToGroupButtonProps();
	const { replaceBlocks } = useDispatch( blockEditorStore );

	const { canRemove, variations } = useSelect(
		( select ) => {
			const { canRemoveBlocks } = select( blockEditorStore );
			const { getBlockVariations } = select( blocksStore );

			return {
				canRemove: canRemoveBlocks( clientIds ),
				// A registered block type has a variations array.
				variations: getBlockVariations(
					groupingBlockName,
					'transform'
				)!,
			};
		},
		[ clientIds, groupingBlockName ]
	);

	// The group button passes its click event instead of a layout.
	const onConvertToGroup = ( layout?: unknown ) => {
		const newBlocks = groupBlocks( blocksSelection, groupingBlockName );

		if ( typeof layout !== 'string' ) {
			layout = 'group';
		}

		if ( newBlocks && newBlocks.length > 0 ) {
			// Because the block is not in the store yet we can't use
			// updateBlockAttributes so need to manually update attributes.
			newBlocks[ 0 ].attributes.layout = layouts[ layout as string ];
			replaceBlocks( clientIds, newBlocks );
		}
	};

	const onConvertToRow = () => onConvertToGroup( 'row' );
	const onConvertToStack = () => onConvertToGroup( 'stack' );
	const onConvertToGrid = () => onConvertToGroup( 'grid' );

	// Don't render the button if the current selection cannot be grouped.
	// A good example is selecting multiple button blocks within a Buttons block:
	// The group block is not a valid child of Buttons, so we should not show the button.
	// Any blocks that are locked against removal also cannot be grouped.
	if ( ! isGroupable || ! canRemove ) {
		return null;
	}

	const canInsertRow = !! variations.find(
		( { name } ) => name === 'group-row'
	);
	const canInsertStack = !! variations.find(
		( { name } ) => name === 'group-stack'
	);
	const canInsertGrid = !! variations.find(
		( { name } ) => name === 'group-grid'
	);

	return (
		<ToolbarGroup>
			<ToolbarButton
				icon={ group }
				label={ _x( 'Group', 'action: convert blocks to group' ) }
				onClick={ onConvertToGroup }
			/>
			{ canInsertRow && (
				<ToolbarButton
					icon={ row }
					label={ _x( 'Row', 'action: convert blocks to row' ) }
					onClick={ onConvertToRow }
				/>
			) }
			{ canInsertStack && (
				<ToolbarButton
					icon={ stack }
					label={ _x( 'Stack', 'action: convert blocks to stack' ) }
					onClick={ onConvertToStack }
				/>
			) }
			{ canInsertGrid && (
				<ToolbarButton
					icon={ grid }
					label={ _x( 'Grid', 'action: convert blocks to grid' ) }
					onClick={ onConvertToGrid }
				/>
			) }
		</ToolbarGroup>
	);
}

export default BlockGroupToolbar;
